import React from 'react';
import LegalPageLayout, { LegalSub, LegalContact } from '../components/legal/LegalPageLayout';
import { useTheme } from '../contexts/ThemeContext';

const CookiePolicyPage = () => {
  const { theme } = useTheme();
  const dark = theme !== 'light';

  const sections = [
    {
      id: 'what-are-cookies',
      title: 'What Are Cookies',
      body: (
        <p>Cookies are small text files that are placed on your device when you visit a website. They are widely used to make websites work more efficiently and to provide information to the website owners. This Cookie Policy explains how FlexiOss Philippines (operated by Fortschritt Medical Supplies Inc.) uses cookies and similar technologies on flexioss.ph.</p>
      ),
    },
    {
      id: 'cookies-we-use',
      title: 'Cookies We Use',
      body: (
        <>
          <LegalSub>Essential Cookies</LegalSub>
          <p>These cookies are strictly necessary for the website to function. They enable core functionality such as page navigation, contact form submission, and cookie consent preferences. You cannot opt out of essential cookies as the website cannot function properly without them.</p>
          <div className={`mt-3 overflow-hidden rounded-xl border ${dark ? 'border-white/10' : 'border-slate-200'}`}>
            <table className="w-full text-xs">
              <thead className={dark ? 'bg-white/[0.04]' : 'bg-slate-50'}>
                <tr className={`border-b ${dark ? 'border-white/10' : 'border-slate-200'}`}>
                  <th className="px-3 py-2.5 text-left font-bold">Cookie</th>
                  <th className="px-3 py-2.5 text-left font-bold">Purpose</th>
                  <th className="px-3 py-2.5 text-left font-bold">Duration</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="px-3 py-2.5 font-mono">flexioss_cookie_consent</td>
                  <td className="px-3 py-2.5">Stores your cookie consent preference</td>
                  <td className="px-3 py-2.5">1 year</td>
                </tr>
              </tbody>
            </table>
          </div>
          <LegalSub>Analytics Cookies</LegalSub>
          <p>These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. Analytics data helps us improve the website experience for medical professionals.</p>
        </>
      ),
    },
    {
      id: 'your-rights',
      title: 'Your Rights Under Philippine Law',
      body: (
        <>
          <p>In accordance with <strong>Republic Act No. 10173 (Data Privacy Act of 2012)</strong> and its Implementing Rules and Regulations (IRR), you have the following rights regarding cookies and personal data collected through them:</p>
          <ul className="premium-list space-y-2.5">
            <li><strong>Right to be Informed</strong> — You have the right to know what personal data is being collected and how it is processed.</li>
            <li><strong>Right to Object</strong> — You may object to the processing of your personal data at any time by declining cookies.</li>
            <li><strong>Right to Access</strong> — You may request access to the personal data collected about you.</li>
            <li><strong>Right to Erasure</strong> — You may request deletion of your personal data.</li>
            <li><strong>Right to Rectification</strong> — You may request correction of inaccurate personal data.</li>
          </ul>
        </>
      ),
    },
    {
      id: 'manage-cookies',
      title: 'How to Manage Cookies',
      body: (
        <>
          <p>You can control and delete cookies through your browser settings. Most browsers allow you to block or delete cookies. However, please note that blocking essential cookies may impact the functionality of this website.</p>
          <p>To manage your cookie preferences on this website, you can use the cookie consent banner that appears on your first visit. You can reset your preference by clearing your browser's local storage for this domain.</p>
        </>
      ),
    },
    {
      id: 'third-party',
      title: 'Third-Party Services',
      body: (
        <>
          <p>Our website embeds content from third-party services that may set their own cookies:</p>
          <ul className="premium-list space-y-2.5">
            <li><strong>YouTube</strong> — Video embeds (Google's cookie policy applies)</li>
            <li><strong>Vimeo</strong> — Video embeds (Vimeo's cookie policy applies)</li>
          </ul>
        </>
      ),
    },
    {
      id: 'contact',
      title: 'Contact Us',
      body: (
        <LegalContact
          intro="For questions about this Cookie Policy or to exercise your data privacy rights under RA 10173, please contact:"
          address="Lot 2 Blk 7 Norwalk St. Northmadison, First Bulacan Industrial City, Malolos City of Bulacan, 3000 Philippines"
          phone={null}
        />
      ),
    },
    {
      id: 'updates',
      title: 'Updates to This Policy',
      body: (
        <p>We may update this Cookie Policy from time to time to reflect changes in our practices or applicable Philippine law. The "Last updated" date at the top of this page indicates when the policy was last revised.</p>
      ),
    },
  ];

  return (
    <LegalPageLayout
      testId="cookie-policy-page"
      title="Cookie Policy"
      subtitle="How FlexiOss® Philippines uses cookies and similar technologies on flexioss.ph."
      updated="February 2026"
      sections={sections}
    />
  );
};

export default CookiePolicyPage;
